import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Box, IconButton } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useFormik } from "formik";
import { object, string } from "yup";
import InfiniteScroll from "react-infinite-scroller";
import { useDispatch, useSelector } from "react-redux";

import BackdropLoader from "../../backdropLoader/BackdropLoader";
import { setNewAddressObj } from "../../../redux/actions";
import {
  addContact,
  addNew,
  addYourFirstContact,
  addYourFirstContactSubMsg,
  contactsLabel,
  deleteContact,
  deleteContactMsg,
  editContact,
  loadingMsg,
  noContactsFoundSubMsg,
  noResultFound,
  search,
} from "../../messages";
import {
  callAPIInterface,
  existingContactLabel,
  newContactLabel,
  noOfRecords,
  paymentMethodTags,
} from "../../constants";
import Button from "../../button/Button";
import Text from "../../text/Text";
import {
  contactPaymentMethods as paymentMethods,
  contactTypes as types,
} from "../../constants";
import ListItemSkeleton from "../../ListItemSkeleton";
import { contactIconGrey, noDataFoundIcon } from "../../images";
import CommonHeader from "../../CommonHeader";
import TruncatedTextTooltip from "../../TruncatedTextTooltip";
import WalletSymbols from "../../WalletSymbols";
import { Tag } from "../../tag/Tag";
import AddContactDrawer from "./AddContactDrawer";
import { SearchBar } from "../../searchBar/SearchBar";
import DeleteDrawer from "./DeleteDrawer";
import ActionsDrawer from "./ActionsDrawer";

const addValidationSchema = object({
  name: string()
    .required("Name is a required field")
    .min(3, "Please enter minimum 3 characters")
    .trim(),
  customer_type: string().required(),
  payment_address: string().required(),
  payment_method: string().required(),
});

const editValidationSchema = object({
  name: string()
    .required("Name is a required field")
    .min(3, "Please enter minimum 3 characters")
    .trim(),
  customer_type: string().required(),
});

const Contacts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const newAddressObj = useSelector((state) => state.common.newAddressObj);

  const [isLoading, setIsLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [openDeleteDrawer, setOpenDeleteDrawer] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextPage, setNextPage] = useState(undefined);
  const [isFetching, setIsFetching] = useState();
  const [buttonLoader, setButtonLoader] = useState(false);
  const [openActionDrawer, setOpenActionDrawer] = useState(false);

  const urlData = new URLSearchParams(location?.search);
  const withdrawRequest = urlData?.get("withdraw_request");
  const type = urlData?.get("type");

  const limitParam = `?limit=${noOfRecords}`;

  const contactOptions = [
    {
      id: 1,
      label: "Edit",
      color: "#0A193E",
      action: () => setOpenAddDrawer(true),
    },
    {
      id: 2,
      label: "Delete",
      color: "#CE3B2C",
      action: () => setOpenDeleteDrawer(true),
    },
  ];

  const getContactByQuery = async (contacts, query) => {
    try {
      const apiData = {
        query: `is:customer ${query}`,
        limit: noOfRecords,
        page: nextPage,
      };

      const res = await callAPIInterface(
        "POST",
        "/search",
        JSON.stringify(apiData)
      );

      setContacts(contacts.concat(res?.data));
      setHasMore(res?.has_more);
      setNextPage(res?.next_page);
    } catch (error) {
      setContacts([]);
      setHasMore(false);
      setNextPage(undefined);
    } finally {
      setIsFetching(false);
    }
  };

  const getContacts = async (contacts, params) => {
    try {
      const res = await callAPIInterface("GET", `/customers${params}`);
      setContacts(contacts.concat(res?.data));
      setHasMore(res?.has_more);
      setIsLoading(false);
      setIsFetching(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await callAPIInterface("DELETE", `/customers/${selectedContact?.id}`);
      setContacts((prev) =>
        prev.filter((contact) => contact.id !== selectedContact?.id)
      );
      setSelectedContact(null);
      setOpenDeleteDrawer(false);
      setOpenActionDrawer(false);
    } catch (error) {
      console.error("Error deleting contact", error);
    }
  };

  const handleAddContact = async (val) => {
    setButtonLoader(true);
    try {
      const apiDataObj = getInputValues(val);
      await callAPIInterface("POST", "/customers", apiDataObj);
      setIsFetching(true);
      if (searchValue) {
        getContactByQuery([], searchValue);
      } else {
        getContacts([], limitParam);
      }
      setOpenAddDrawer(false);
      resetForm();
    } catch (error) {
      console.error("Error adding contact", error);
    } finally {
      setButtonLoader(false);
      dispatch(setNewAddressObj({}));
    }
  };

  const handleUpdateContact = async (val) => {
    setButtonLoader(true);
    try {
      const apiDataObj = getInputValues(val, true);
      const res = await callAPIInterface(
        "POST",
        `/customers/${selectedContact?.id}`,
        apiDataObj
      );
      setContacts((prev) => {
        prev[selectedContact?.index] = res;
        return [...prev];
      });
      setOpenAddDrawer(false);
      setSelectedContact(null);
      setOpenActionDrawer(false);
    } catch (error) {
      console.error(error);
    } finally {
      setButtonLoader(false);
    }
  };

  const getInputValues = (values, isEdit = false) => {
    const { name, customer_type, payment_address, payment_method } = values;

    const paymentMethodObj =
      !isEdit &&
      paymentMethods.find((method) => method.label === payment_method);

    let customerType = customer_type?.toLowerCase();

    customerType = isEdit
      ? { type: customerType }
      : { customer_type: customerType };

    return {
      name,
      ...customerType,
      ...(!isEdit && {
        payment_address_details: [
          {
            payment_address,
            payment_method: paymentMethodObj?.payment_method,
            target_currency: paymentMethodObj?.target_currency,
          },
        ],
      }),
    };
  };

  const contactItem = useCallback(
    (contact, index) => {
      const name = contact?.name?.split(" ");
      let initials = name?.[0]?.charAt(0) + (name?.[1]?.charAt(0) || "");
      initials = initials?.toUpperCase() ?? "#";

      const customerType = types.find(
        (type) =>
          type.value === contact?.customer_type?.toLowerCase() ||
          type.value === contact?.type?.toLowerCase()
      )?.label;

      const paymentMethodTypes =
        [
          ...new Set(
            contact?.payment_address_details?.map(
              (detail) => detail.payment_method
            )
          ),
        ] || [];

      return (
        <Box
          className="address-list-item"
          key={contact.id}
          sx={{
            backgroundColor: "#fff",
            border: "0",
            padding: "10px 0px",
          }}
          onClick={() => {
            navigate(`/settings/contacts/${contact?.id}`);
          }}
        >
          <Avatar
            sx={{
              backgroundColor: "#F7FAFC",
              border: "1px solid #F6F3F3",
            }}
          >
            <Text size={14} font="medium" sx={{ color: "#0A193E" }}>
              {initials}
            </Text>
          </Avatar>

          <Box display="flex" flexDirection="column">
            <TruncatedTextTooltip
              textProps={{
                font: "semibold",
                width: {
                  xs: "200px",
                  sm: "350px",
                  md: "400px",
                },
              }}
              textValue={contact?.name}
              fontSize={16}
            />
            <Text size={14} font="regular" className="grey-text">
              {customerType}
            </Text>
            <Box display="flex" flexDirection="row" gap={0.5}>
              {paymentMethodTypes?.map((paymentMethodType) => (
                <Tag
                  text={paymentMethodTags[paymentMethodType]?.title}
                  font="medium"
                  className="payment-method-tag"
                  style={{
                    background:
                      paymentMethodTags[paymentMethodType]?.background,
                  }}
                />
              ))}
            </Box>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setSelectedContact({ ...contact, index });
              setOpenActionDrawer(true);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      );
    },
    [anchorEl]
  );

  const loadMore = () => {
    const params = `${limitParam}&ending_before=${
      contacts[contacts.length - 1].id
    }`;

    if (hasMore) {
      if (searchValue) getContactByQuery(contacts, searchValue);
      else getContacts(contacts, params);
    }
  };

  const resetNewAddressState = useCallback(() => {
    dispatch(setNewAddressObj({}));
    setSelectedContact(null);
  }, [dispatch]);

  const renderContacts = useCallback(() => {
    if (isFetching) {
      return <ListItemSkeleton />;
    }

    if (!contacts.length) {
      return searchValue ? (
        <>
          <Box className="no-data-container" padding="30px">
            <img src={noDataFoundIcon} height={44} width={44} />
            <Text size={20} font="bold">
              {noResultFound}
            </Text>
            <Text className="grey-text" size={16} font="regular">
              {noContactsFoundSubMsg}
            </Text>
          </Box>
        </>
      ) : (
        <Box className="no-data-container" padding="30px">
          <img src={contactIconGrey} height={44} width={44} />
          <Text size={20} font="bold">
            {addYourFirstContact}
          </Text>
          <Text className="grey-text" size={16} font="regular">
            {addYourFirstContactSubMsg}
          </Text>
          <Button
            sx={{ width: "150px", padding: "12px 24px !important" }}
            label={addNew}
            variant="outlined"
            onClick={() => {
              resetNewAddressState();
              setOpenAddDrawer(true);
            }}
          />
        </Box>
      );
    }

    return (
      <>
        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          initialLoad={true}
          hasMore={hasMore}
          loader={<ListItemSkeleton />}
          useWindow={false}
        >
          {contacts?.map((contact, index) => contactItem(contact, index))}
        </InfiniteScroll>
      </>
    );
  }, [isFetching, searchValue, contacts, anchorEl, hasMore, loadMore]);

  const addFormik = useFormik({
    initialValues: {
      customer_type: "",
      name: "",
      payment_address: "",
      payment_method: "",
    },
    validationSchema: addValidationSchema,
    enableReinitialize: true,
    onSubmit: handleAddContact,
  });

  const editFormik = useFormik({
    initialValues: {
      customer_type: types.find(
        (type) =>
          type?.value === selectedContact?.customer_type?.toLowerCase() ||
          type?.value === selectedContact?.type?.toLowerCase()
      )?.label,
      name: selectedContact?.name,
    },
    validationSchema: editValidationSchema,
    enableReinitialize: true,
    onSubmit: handleUpdateContact,
  });

  const actionButtonComponent = (
    <WalletSymbols
      customClass="cursor-pointer"
      sx={{ color: "#0a193e" }}
      symbol="add"
      action={() => {
        resetNewAddressState();
        setOpenAddDrawer(true);
      }}
    />
  );

  const { setFieldValue, resetForm } = addFormik;

  useEffect(() => {
    if (searchValue) {
      if (searchValue?.length >= 3) {
        const debouncedSearch = setTimeout(() => {
          setIsFetching(true);
          getContactByQuery([], searchValue);
        }, 500);

        return () => clearTimeout(debouncedSearch);
      }
    } else {
      setIsFetching(true);
      getContacts([], limitParam);
    }
  }, [searchValue]);

  useEffect(() => {
    if (withdrawRequest && type) {
      dispatch(
        setNewAddressObj({
          withdraw_request: withdrawRequest,
          type: type,
        })
      );
    }
  }, [withdrawRequest, type]);

  useEffect(() => {
    const { withdraw_request, type } = newAddressObj;

    if (withdraw_request && type) {
      setFieldValue("payment_address", withdraw_request);
      if (type === newContactLabel) {
        setOpenAddDrawer(true);
        dispatch(setNewAddressObj({}));
      }
    }
  }, [newAddressObj]);

  return isLoading ? (
    <BackdropLoader
      open={isLoading}
      alt={loadingMsg}
      text={loadingMsg}
      customClass="loading-in-auth"
    />
  ) : (
    <>
      <CommonHeader
        textAfterIcon={contactsLabel}
        icon="backArrow"
        navigation="/settings"
        rightSideComponent={
          (!type || type !== existingContactLabel || !contacts.length) &&
          actionButtonComponent
        }
      />
      <Box className="options-container">
        <Box className="advance-settings-wrapper">
          <Box width="100%">
            <SearchBar
              value={searchValue}
              handleChange={(e) => setSearchValue(e?.target?.value)}
              handleClear={() => setSearchValue("")}
              placeHolder={search}
            />
          </Box>
          <Box
            className="module-container"
            display="flex"
            flexDirection="column"
            gap={2}
          >
            {renderContacts()}
          </Box>
          <ActionsDrawer
            openDrawer={openActionDrawer}
            setOpenDrawer={setOpenActionDrawer}
            actionOptions={contactOptions}
          />
        </Box>

        <AddContactDrawer
          formik={selectedContact?.id ? editFormik : addFormik}
          modalHeader={selectedContact?.id ? editContact : addContact}
          buttonLoader={buttonLoader}
          setButtonLoader={setButtonLoader}
          disabledFields={["payment_method"]}
          openDrawer={openAddDrawer}
          setOpenDrawer={setOpenAddDrawer}
          setActionDrawer={setOpenActionDrawer}
        />
        <DeleteDrawer
          openDrawer={openDeleteDrawer}
          setOpenDrawer={setOpenDeleteDrawer}
          deleteTitle={deleteContact}
          deleteSubText={deleteContactMsg}
          handleDelete={handleDelete}
          setActionDrawer={setOpenActionDrawer}
        />
      </Box>
    </>
  );
};
export default Contacts;
